import { useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ArrowsCounterClockwise, Info } from '@phosphor-icons/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Skeleton } from 'components/ui/skeleton';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'components/ui/hover-card';
import { useBuildingsAndUnits } from 'hooks/useBuildingsAndUnits';
import { useHerverhuurPipeline } from './hooks/useHerverhuurPipeline';
import { PipelineColumn } from './components/PipelineColumn';
import { TerminationCard } from './components/TerminationCard';
import { VacantCard } from './components/VacantCard';
import { CandidatesCard } from './components/CandidatesCard';
import { IncomingInspectionCard } from './components/IncomingInspectionCard';

const COLUMNS = [
  {
    key: 'inTermination' as const,
    title: 'In Opzeg',
    accentClass: 'text-amber-700',
    headerBgClass: 'bg-amber-100',
    emptyMessage: 'Geen contracten in opzeg',
    navigateTo: '/rental/outgoing-tenants',
  },
  {
    key: 'outgoingInspection' as const,
    title: 'Uitgaande PB',
    accentClass: 'text-orange-700',
    headerBgClass: 'bg-orange-100',
    emptyMessage: 'Geen uitgaande plaatsbeschrijvingen actief',
    navigateTo: '/rental/outgoing-tenants',
  },
  {
    key: 'vacant' as const,
    title: 'Leegstand',
    accentClass: 'text-neutral-primary',
    headerBgClass: 'bg-neutral-secondary-light',
    emptyMessage: 'Geen leegstaande units',
    navigateTo: '/rental/vacancies',
  },
  {
    key: 'withCandidates' as const,
    title: 'Kandidaten',
    accentClass: 'text-blue-700',
    headerBgClass: 'bg-blue-50',
    emptyMessage: 'Geen units met actieve kandidaten',
    navigateTo: '/rental/candidacies',
  },
  {
    key: 'incomingInspection' as const,
    title: 'Ingaande PB',
    accentClass: 'text-moss-primary',
    headerBgClass: 'bg-moss-secondary-light',
    emptyMessage: 'Geen ingaande plaatsbeschrijvingen actief',
    navigateTo: '/rental/incoming-tenants',
  },
];

const ColumnSkeleton = () => (
  <div className="flex flex-col w-[300px] flex-shrink-0 bg-neutral-secondary-light rounded-lg overflow-hidden gap-2 p-2">
    <Skeleton className="h-8 w-full rounded-none" />
    {[1, 2, 3].map(i => (
      <Skeleton key={i} className="h-32 w-full rounded-lg" />
    ))}
  </div>
);

const HerverhuurOverzicht = () => {
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('all');
  const { buildings } = useBuildingsAndUnits();
  const { pipeline, isLoading } = useHerverhuurPipeline(
    selectedBuildingId !== 'all' ? selectedBuildingId : undefined
  );

  const ownedBuildings = buildings.filter(b => b.owner);

  const totalInPipeline =
    (pipeline?.inTermination.length ?? 0) +
    (pipeline?.outgoingInspection.length ?? 0) +
    (pipeline?.vacant.length ?? 0) +
    (pipeline?.withCandidates.length ?? 0) +
    (pipeline?.incomingInspection.length ?? 0);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Overzicht Herverhuur | Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white flex-shrink-0 z-10">
          <div className="flex items-center gap-2">
            <ArrowsCounterClockwise className="text-neutral-secondary-mid" size={18} weight="duotone" />
            <div>
              <div className="flex items-center gap-1.5">
                <h1 className="text-sm font-medium text-neutral-primary">Overzicht Herverhuur</h1>
                <HoverCard openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <button className="text-blue-500 hover:text-blue-600 transition-colors">
                      <Info size={18} weight="fill" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 p-4" align="start" side="bottom">
                    <p className="text-xs font-semibold text-neutral-primary mb-3">Wanneer staat een unit in welke fase?</p>
                    <div className="flex flex-col gap-2.5">
                      <PhaseInfo
                        color="bg-amber-400"
                        label="In Opzeg"
                        description="De huurder heeft opgezegd maar de uitgaande plaatsbeschrijving is nog niet opgestart."
                      />
                      <PhaseInfo
                        color="bg-orange-400"
                        label="Uitgaande PB"
                        description="De uitgaande plaatsbeschrijving is opgestart. Een concrete inspectiedatum hoeft nog niet bepaald te zijn."
                      />
                      <PhaseInfo
                        color="bg-neutral-400"
                        label="Leegstand"
                        description="De unit heeft geen lopend contract en staat beschikbaar voor verhuur."
                      />
                      <PhaseInfo
                        color="bg-blue-400"
                        label="Kandidaten"
                        description="Er zijn één of meer actieve kandidaten voor de unit (geïnteresseerd of goedgekeurd)."
                      />
                      <PhaseInfo
                        color="bg-moss-primary"
                        label="Ingaande PB"
                        description="Er is een nieuw contract in opmaak via een goedgekeurde kandidaat. De ingaande plaatsbeschrijving loopt."
                      />
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <p className="text-xs text-neutral-secondary-mid">
                {isLoading ? 'Laden…' : `${totalInPipeline} units in het herverhuurproces`}
              </p>
            </div>
          </div>
          <Select value={selectedBuildingId} onValueChange={setSelectedBuildingId} {...({ modal: false } as Record<string, unknown>)}>
            <SelectTrigger className="w-52 h-8 text-xs">
              <SelectValue placeholder="Alle gebouwen" />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4} align="start" className="max-h-[220px] overflow-y-auto">
              <SelectItem value="all" className="text-xs">Alle gebouwen</SelectItem>
              {ownedBuildings.map(b => (
                <SelectItem key={b.id} value={b.id} className="text-xs">
                  {b.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-3 p-4 overflow-x-auto flex-1 min-h-0">
          {isLoading ? (
            COLUMNS.map(col => <ColumnSkeleton key={col.key} />)
          ) : (
            <>
              <PipelineColumn
                title={COLUMNS[0].title}
                count={pipeline?.inTermination.length ?? 0}
                accentClass={COLUMNS[0].accentClass}
                headerBgClass={COLUMNS[0].headerBgClass}
                emptyMessage={COLUMNS[0].emptyMessage}
                navigateTo={COLUMNS[0].navigateTo}
              >
                {pipeline?.inTermination.map(unit => (
                  <TerminationCard key={unit.contractId} unit={unit} step={1} />
                ))}
              </PipelineColumn>

              <PipelineColumn
                title={COLUMNS[1].title}
                count={pipeline?.outgoingInspection.length ?? 0}
                accentClass={COLUMNS[1].accentClass}
                headerBgClass={COLUMNS[1].headerBgClass}
                emptyMessage={COLUMNS[1].emptyMessage}
                navigateTo={COLUMNS[1].navigateTo}
              >
                {pipeline?.outgoingInspection.map(unit => (
                  <TerminationCard key={unit.contractId} unit={unit} step={2} />
                ))}
              </PipelineColumn>

              <PipelineColumn
                title={COLUMNS[2].title}
                count={pipeline?.vacant.length ?? 0}
                accentClass={COLUMNS[2].accentClass}
                headerBgClass={COLUMNS[2].headerBgClass}
                emptyMessage={COLUMNS[2].emptyMessage}
                navigateTo={COLUMNS[2].navigateTo}
              >
                {pipeline?.vacant.map(unit => (
                  <VacantCard key={unit.unitId} unit={unit} />
                ))}
              </PipelineColumn>

              <PipelineColumn
                title={COLUMNS[3].title}
                count={pipeline?.withCandidates.length ?? 0}
                accentClass={COLUMNS[3].accentClass}
                headerBgClass={COLUMNS[3].headerBgClass}
                emptyMessage={COLUMNS[3].emptyMessage}
                navigateTo={COLUMNS[3].navigateTo}
              >
                {pipeline?.withCandidates.map(unit => (
                  <CandidatesCard key={unit.unitId} unit={unit} />
                ))}
              </PipelineColumn>

              <PipelineColumn
                title={COLUMNS[4].title}
                count={pipeline?.incomingInspection.length ?? 0}
                accentClass={COLUMNS[4].accentClass}
                headerBgClass={COLUMNS[4].headerBgClass}
                emptyMessage={COLUMNS[4].emptyMessage}
                navigateTo={COLUMNS[4].navigateTo}
              >
                {pipeline?.incomingInspection.map(unit => (
                  <IncomingInspectionCard key={unit.contractId} unit={unit} />
                ))}
              </PipelineColumn>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const PhaseInfo = ({ color, label, description }: { color: string; label: string; description: string }) => (
  <div className="flex gap-2">
    <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${color}`} />
    <div>
      <p className="text-[11px] font-medium text-neutral-primary leading-tight">{label}</p>
      <p className="text-[11px] text-neutral-secondary-mid leading-snug">{description}</p>
    </div>
  </div>
);

export default HerverhuurOverzicht;
